import React, { useState, useEffect, useRef } from 'react';
import Papa from 'papaparse';
import {
  Upload, CheckCircle, AlertCircle, Trash2, RefreshCw, Eye, EyeOff, Download,
} from 'lucide-react';
import {
  collection, query, where, getDocs, doc, updateDoc, deleteDoc, orderBy,
} from 'firebase/firestore';
import { db } from '../../../../firebase';
import {
  normalizeTollRecord, validateTollRecord, generateTollDocId,
  formatCLP, TOLL_ESTADO_BADGE, TOLL_TARIFA_BADGE,
} from '../../../../utils/tollHelpers';
import {
  uploadTollRecords, countExistingTolls, fetchTollsChile,
} from '../../../../services/tollsService';

// ─── Styles ───────────────────────────────────────────────────────────────────
const cls = {
  label: 'block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1',
  input: 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-400',
  select: 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-400',
  btnPrimary: 'rounded-xl bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition',
  btnDanger: 'rounded-xl bg-red-600 px-4 py-2 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-50 transition',
  btnSecondary: 'rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 transition',
};

// ─── CSV Parser ───────────────────────────────────────────────────────────────
function parseCsvFile(file) {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      encoding: 'UTF-8',
      complete: (results) => resolve(results),
      error: (err) => reject(err),
    });
  });
}

// ─── Upload Tab ───────────────────────────────────────────────────────────────
function UploadTab() {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [normalized, setNormalized] = useState([]);
  const [errors, setErrors] = useState([]);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [existingCount, setExistingCount] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | parsing | preview | uploading | done | error
  const [progress, setProgress] = useState({ phase: '', written: 0, deleted: 0, total: 0 });
  const [uploadResult, setUploadResult] = useState(null);
  const [showPreview, setShowPreview] = useState(true);

  useEffect(() => {
    countExistingTolls(2026).then(setExistingCount);
  }, []);

  const handleFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setStatus('parsing');
    setErrors([]);
    try {
      const result = await parseCsvFile(f);
      setParsed(result);

      const recs = [];
      const errs = [];
      result.data.forEach((row, idx) => {
        const rec = normalizeTollRecord(row);
        const { valid, errors: rowErrors } = validateTollRecord(rec);
        if (!valid) errs.push({ row: idx + 2, errors: rowErrors });
        recs.push(rec);
      });
      setNormalized(recs);
      setErrors(errs);
      setStatus('preview');
    } catch (err) {
      setErrors([{ row: 0, errors: [err.message] }]);
      setStatus('error');
    }
  };

  const handleUpload = async () => {
    if (!normalized.length) return;
    if (replaceExisting && existingCount > 0) {
      const ok = window.confirm(
        `Esta acción eliminará los ${existingCount} peajes actuales del año 2026 y cargará los ${normalized.length} nuevos registros del archivo.\n\n¿Continuar?`
      );
      if (!ok) return;
    }
    setStatus('uploading');
    try {
      const result = await uploadTollRecords(normalized, {
        replaceExisting,
        year: 2026,
        onProgress: (p) => setProgress(p),
      });
      setUploadResult(result);
      setStatus('done');
      countExistingTolls(2026).then(setExistingCount);
    } catch (err) {
      setErrors([{ row: 0, errors: [err.message] }]);
      setStatus('error');
    }
  };

  const reset = () => {
    setFile(null);
    setParsed(null);
    setNormalized([]);
    setErrors([]);
    setStatus('idle');
    setUploadResult(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="space-y-5">
      {/* Info actual */}
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 flex items-center justify-between">
        <div>
          <p className="text-sm font-bold text-slate-700">Peajes 2026 en Firestore</p>
          <p className="mt-0.5 text-2xl font-black text-slate-900">{existingCount ?? '…'}</p>
        </div>
        <RefreshCw
          className="h-5 w-5 text-slate-400 cursor-pointer hover:text-blue-600 transition"
          onClick={() => countExistingTolls(2026).then(setExistingCount)}
        />
      </div>

      {/* Selector de archivo */}
      {status === 'idle' && (
        <div className="rounded-2xl border-2 border-dashed border-slate-300 bg-white p-8 text-center hover:border-blue-400 transition">
          <Upload className="mx-auto h-8 w-8 text-slate-400" />
          <p className="mt-2 text-sm font-bold text-slate-700">
            Seleccionar archivo CSV o JSON
          </p>
          <p className="mt-1 text-xs text-slate-500">peajes_chile_2026.csv</p>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.json"
            onChange={handleFileChange}
            className="hidden"
            id="toll-file-input"
          />
          <label htmlFor="toll-file-input" className={`${cls.btnPrimary} mt-4 inline-block cursor-pointer`}>
            Seleccionar archivo
          </label>
        </div>
      )}

      {/* Parsing */}
      {status === 'parsing' && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center">
          <div className="animate-spin mx-auto h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full" />
          <p className="mt-2 text-sm text-slate-600">Analizando archivo…</p>
        </div>
      )}

      {/* Preview */}
      {status === 'preview' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-bold text-slate-700">
                {normalized.length} registros encontrados
                {errors.length > 0 && (
                  <span className="ml-2 text-amber-600">· {errors.length} con errores</span>
                )}
              </p>
              <p className="text-xs text-slate-500">{file?.name}</p>
            </div>
            <div className="flex gap-2">
              <button className={cls.btnSecondary} onClick={() => setShowPreview(v => !v)}>
                {showPreview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
              <button className={cls.btnSecondary} onClick={reset}>Cancelar</button>
            </div>
          </div>

          {/* Errors */}
          {errors.length > 0 && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs font-bold text-amber-700 mb-1">Errores de validación</p>
              {errors.slice(0, 5).map((e, i) => (
                <p key={i} className="text-xs text-amber-600">
                  Fila {e.row}: {e.errors.join(', ')}
                </p>
              ))}
              {errors.length > 5 && (
                <p className="text-xs text-amber-500">…y {errors.length - 5} errores más</p>
              )}
            </div>
          )}

          {/* Preview table */}
          {showPreview && (
            <div className="overflow-x-auto rounded-2xl border border-slate-200">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    {['Ruta','Peaje','Km','Categoría','Tarifa','2026','Estado'].map(h => (
                      <th key={h} className="px-3 py-2 text-left font-bold text-slate-600">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {normalized.slice(0, 20).map((rec, i) => (
                    <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-1.5 font-medium text-slate-800 max-w-[120px] truncate">{rec.ruta}</td>
                      <td className="px-3 py-1.5 text-slate-700">{rec.nombrePeaje}</td>
                      <td className="px-3 py-1.5 text-slate-500">{rec.km ?? '-'}</td>
                      <td className="px-3 py-1.5 text-slate-600">{rec.categoriaVehiculo}</td>
                      <td className="px-3 py-1.5 text-slate-600">{rec.tipoTarifa}</td>
                      <td className="px-3 py-1.5 font-bold text-slate-900">{formatCLP(rec.tarifa2026)}</td>
                      <td className="px-3 py-1.5">
                        <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold ${(TOLL_ESTADO_BADGE[rec.estado] || TOLL_ESTADO_BADGE.base_validacion).cls}`}>
                          {(TOLL_ESTADO_BADGE[rec.estado] || TOLL_ESTADO_BADGE.base_validacion).label}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {normalized.length > 20 && (
                <p className="px-3 py-2 text-xs text-slate-400">
                  Mostrando 20 de {normalized.length} registros
                </p>
              )}
            </div>
          )}

          {/* Options and upload */}
          <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={replaceExisting}
                onChange={(e) => setReplaceExisting(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-blue-600"
              />
              <span className="text-sm font-semibold text-slate-700">
                Reemplazar peajes existentes del año 2026
                {existingCount !== null && (
                  <span className="ml-1 text-slate-400">({existingCount} registros actuales)</span>
                )}
              </span>
            </label>
            {replaceExisting && (
              <p className="text-xs text-red-600 rounded-xl bg-red-50 px-3 py-2">
                Se eliminarán {existingCount} registros existentes y se cargarán {normalized.length} nuevos.
              </p>
            )}
            <button
              className={cls.btnPrimary}
              onClick={handleUpload}
              disabled={normalized.length === 0}
            >
              Cargar {normalized.length} registros a Firestore
            </button>
          </div>
        </div>
      )}

      {/* Uploading */}
      {status === 'uploading' && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center space-y-2">
          <div className="animate-spin mx-auto h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full" />
          <p className="text-sm font-bold text-slate-700">
            {progress.phase === 'deleting'
              ? `Eliminando… ${progress.deleted}/${progress.total}`
              : `Cargando… ${progress.written}/${progress.total}`}
          </p>
        </div>
      )}

      {/* Done */}
      {status === 'done' && uploadResult && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 space-y-2">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-emerald-600" />
            <p className="font-bold text-emerald-800">Carga completada</p>
          </div>
          <p className="text-sm text-emerald-700">
            {uploadResult.deleted > 0 && `${uploadResult.deleted} eliminados · `}
            {uploadResult.written} registros guardados en peajes_chile
          </p>
          <button className={cls.btnSecondary} onClick={reset}>Cargar otro archivo</button>
        </div>
      )}

      {/* Error */}
      {status === 'error' && errors.length > 0 && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 space-y-2">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <p className="font-bold text-red-800">Error</p>
          </div>
          {errors.map((e, i) => (
            <p key={i} className="text-sm text-red-700">{e.errors.join(', ')}</p>
          ))}
          <button className={cls.btnSecondary} onClick={reset}>Reintentar</button>
        </div>
      )}
    </div>
  );
}

// ─── Table Tab ────────────────────────────────────────────────────────────────
function TableTab() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterRuta, setFilterRuta] = useState('');
  const [filterCategoria, setFilterCategoria] = useState('');
  const [filterTarifa, setFilterTarifa] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const CATEGORIAS = ['moto', 'auto_camioneta', 'auto_con_remolque', 'bus_2_ejes', 'camion_2_ejes', 'camion_mas_2_ejes'];
  const TARIFAS = ['normal', 'punta', 'saturacion', 'festivo', 'fin_de_semana', 'valle'];

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchTollsChile({ year: 2026, activeOnly: false });
      setRecords(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = records.filter((r) => {
    if (filterRuta && !r.ruta?.toLowerCase().includes(filterRuta.toLowerCase())) return false;
    if (filterCategoria && r.categoriaVehiculo !== filterCategoria) return false;
    if (filterTarifa && r.tipoTarifa !== filterTarifa) return false;
    return true;
  });

  const rutas = [...new Set(records.map((r) => r.ruta).filter(Boolean))].sort();

  const handleSaveEdit = async () => {
    if (!editingId) return;
    try {
      await updateDoc(doc(db, 'peajes_chile', editingId), {
        ...editForm,
        actualizadoEn: new Date().toISOString(),
      });
      setEditingId(null);
      load();
    } catch (err) {
      alert('Error al guardar: ' + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este registro de peaje?')) return;
    await deleteDoc(doc(db, 'peajes_chile', id));
    load();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[160px]">
          <label className={cls.label}>Ruta</label>
          <select className={cls.select} value={filterRuta} onChange={(e) => setFilterRuta(e.target.value)}>
            <option value="">Todas</option>
            {rutas.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <label className={cls.label}>Categoría</label>
          <select className={cls.select} value={filterCategoria} onChange={(e) => setFilterCategoria(e.target.value)}>
            <option value="">Todas</option>
            {CATEGORIAS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className={cls.label}>Tarifa</label>
          <select className={cls.select} value={filterTarifa} onChange={(e) => setFilterTarifa(e.target.value)}>
            <option value="">Todas</option>
            {TARIFAS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="ml-auto flex gap-2">
          <span className="text-sm text-slate-500 self-end pb-2">{filtered.length} registros</span>
          <button className={cls.btnSecondary} onClick={load}>
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-2xl border border-slate-200">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              {['Ruta', 'Peaje', 'Km', 'Categoría', 'Tipo', 'Tarifa 2026', 'Estado', 'Activo', ''].map((h) => (
                <th key={h} className="px-3 py-2 text-left font-bold text-slate-600 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 200).map((rec) => {
              const isEditing = editingId === rec.id;
              return (
                <tr key={rec.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-1.5 font-medium text-slate-800 max-w-[120px] truncate">{rec.ruta}</td>
                  <td className="px-3 py-1.5 text-slate-700">{rec.nombrePeaje}</td>
                  <td className="px-3 py-1.5 text-slate-500">{rec.km ?? '-'}</td>
                  <td className="px-3 py-1.5 text-slate-600">{rec.categoriaVehiculo}</td>
                  <td className="px-3 py-1.5">
                    <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold ${(TOLL_TARIFA_BADGE[rec.tipoTarifa] || TOLL_TARIFA_BADGE.normal).cls}`}>
                      {(TOLL_TARIFA_BADGE[rec.tipoTarifa] || TOLL_TARIFA_BADGE.normal).label}
                    </span>
                  </td>
                  <td className="px-3 py-1.5">
                    {isEditing ? (
                      <input
                        type="number"
                        className={cls.input + ' w-24'}
                        value={editForm.tarifa2026 ?? rec.tarifa2026}
                        onChange={(e) => setEditForm((f) => ({ ...f, tarifa2026: Number(e.target.value) }))}
                      />
                    ) : (
                      <span className="font-bold text-slate-900">{formatCLP(rec.tarifa2026)}</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5">
                    <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold ${(TOLL_ESTADO_BADGE[rec.estado] || TOLL_ESTADO_BADGE.base_validacion).cls}`}>
                      {(TOLL_ESTADO_BADGE[rec.estado] || TOLL_ESTADO_BADGE.base_validacion).label}
                    </span>
                  </td>
                  <td className="px-3 py-1.5">
                    <span className={`inline-block h-2 w-2 rounded-full ${rec.activo ? 'bg-emerald-400' : 'bg-slate-300'}`} />
                  </td>
                  <td className="px-3 py-1.5">
                    {isEditing ? (
                      <div className="flex gap-1">
                        <button className="text-emerald-600 hover:text-emerald-800 font-bold text-xs" onClick={handleSaveEdit}>Guardar</button>
                        <button className="text-slate-500 hover:text-slate-700 text-xs" onClick={() => setEditingId(null)}>Cancelar</button>
                      </div>
                    ) : (
                      <div className="flex gap-1">
                        <button
                          className="text-blue-600 hover:text-blue-800 text-xs font-bold"
                          onClick={() => { setEditingId(rec.id); setEditForm({ tarifa2026: rec.tarifa2026, activo: rec.activo }); }}
                        >
                          Editar
                        </button>
                        <button
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDelete(rec.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length > 200 && (
          <p className="px-3 py-2 text-xs text-slate-400">
            Mostrando 200 de {filtered.length} registros. Usa los filtros para acotar.
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function PeajesAdminPanel() {
  const [tab, setTab] = useState('upload');

  const TABS = [
    { id: 'upload', label: 'Cargar archivo' },
    { id: 'table', label: 'Ver / Editar peajes' },
  ];

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-base font-bold text-slate-900">Gestión de Peajes Chile 2026</h3>
        <p className="mt-0.5 text-sm text-slate-500">
          Carga el CSV de peajes, previsualiza y sube a Firestore. Colección: <code className="text-xs bg-slate-100 px-1 rounded">peajes_chile</code>
        </p>
      </div>

      <div className="flex gap-1 border-b border-slate-200">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-semibold rounded-t-lg border border-b-0 transition ${
              tab === t.id
                ? 'border-slate-200 bg-white text-blue-700'
                : 'border-transparent bg-slate-100 text-slate-500 hover:bg-slate-200'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div>
        {tab === 'upload' && <UploadTab />}
        {tab === 'table' && <TableTab />}
      </div>
    </div>
  );
}
